/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The buyer picking which of the bought seats is theirs.
 *
 * Nothing seats the owner automatically — their allowance is sized by the
 * seat, so they say which one it is. Shown wherever the owner can land while
 * still unseated: the invite screen after activation, and Settings · Team.
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';

/** The block shape the seat accounting answers in. */
export interface OwnerSeatBlock {
  plan_id: string;
  free: number;
  bought?: number;
  taken?: number;
}

/**
 * Whether the unseated owner can take a seat in this block.
 *
 * Their own floating chair pays for whichever block they pick, so the test
 * is the block's local room (bought - taken) — the clamped `free` already
 * charges their chair somewhere and would lock the very tier they bought.
 */
const noRoom = (block: OwnerSeatBlock): boolean => (
  block.bought != null && block.taken != null
    ? block.bought - block.taken <= 0
    : block.free <= 0
);

export const OwnerSeatPick: React.FC<{
  blocks: OwnerSeatBlock[];
  planTitle: (planId: string) => string | null;
  /** Seats the owner; resolves false when the server refused. */
  onChoose: (planId: string) => Promise<boolean>;
  /** Where the card sits: the team tab drops "change it later on the team tab". */
  context?: 'invite' | 'team';
  /** The gate centers everything; the inline cards stay left-aligned. */
  centered?: boolean;
}> = ({ blocks, planTitle, onChoose, context = 'invite', centered = false }) => {
  const { t } = useTranslation('@pages/onboarding');
  const [choosing, setChoosing] = useState(false);

  const choose = useCallback(async (planId: string) => {
    if (choosing) return;
    setChoosing(true);
    try {
      await onChoose(planId);
    } finally {
      setChoosing(false);
    }
  }, [choosing, onChoose]);

  if (!blocks.length) return null;
  return (
    <div
      data-testid="owner-seat-pick"
      style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--space-2-5)',
        padding: 'var(--space-3) var(--space-3-5)',
        borderRadius: 'var(--radius-lg)', background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        textAlign: centered ? 'center' : undefined,
      }}
    >
      <Text
        component="span"
        style={{
          fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)',
          color: 'var(--text-primary)',
        }}
      >
        {t('inviteTeam.ownerPickTitle')}
      </Text>
      <Text
        component="span"
        style={{
          fontSize: 'var(--text-small)', lineHeight: 1.4,
          color: 'var(--text-secondary)', textWrap: 'pretty',
        }}
      >
        {t(context === 'team'
          ? 'inviteTeam.ownerPickHintHere'
          : 'inviteTeam.ownerPickHint')}
      </Text>
      <div
        style={{
          display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap',
          justifyContent: centered ? 'center' : undefined,
        }}
      >
        {blocks.map((block) => {
          const title = planTitle(block.plan_id) ?? block.plan_id;
          const full = noRoom(block);
          return (
            <UnstyledButton
              key={block.plan_id}
              data-testid={`owner-seat-pick-${block.plan_id}`}
              disabled={full || choosing}
              onClick={() => choose(block.plan_id)}
              style={{
                padding: 'var(--space-1-5) var(--space-3)',
                borderRadius: 'var(--radius-full)',
                border: '1px solid var(--border-subtle)',
                background: 'var(--surface-primary)',
                fontSize: 'var(--text-small)',
                fontWeight: 'var(--font-bold)',
                color: full ? 'var(--text-tertiary-soft)' : 'var(--seed-base)',
                cursor: full || choosing ? 'not-allowed' : 'pointer',
                opacity: choosing ? 0.6 : 1,
              }}
            >
              {full ? t('inviteTeam.ownerPickFull', { plan: title }) : title}
            </UnstyledButton>
          );
        })}
      </div>
    </div>
  );
};
